import { useEffect, useMemo, useState, type FormEvent } from "react";
import { apiMutate, apiMutateMitZusatz } from "../hooks/useApi";
import { Button, Checkbox, DateInput, ErrorNote, Input, Modal, Segmente } from "./components";
import { money } from "./format";
import { angabenFehlenAusFehler, angabenVollstaendig, eingabeAus, RechnungsangabenAbschnitt, rechnungsangabenBody, type RechnungsEingabe, type RechnungsFehlt, type RechnungsKundenAngaben } from "./RechnungsangabenAbschnitt";
import { VariantenKarten, type VarianteOption } from "./VarianteWaehlenDialog";

/**
 * Bezahlt-Dialog (Einheit D der Rechnungs-Automatik, Mockup freigegeben von Antoine am 26.10.2026).
 * Antoine: „Wenn der Betreiber auf Bezahlt klickt, muss auch eine Rechnung rausgehen, gibt es
 * Nachfragen dazu, müssen diese im selben Dialog gestellt werden. Ab jetzt.“
 *
 * Ersetzt den früheren Bestätigungsdialog. Alles, was der Server für die Rechnung noch braucht
 * (Menü bei mehreren Varianten, Rechnungsadresse, E-Mail), wird HIER gefragt und mit dem Klick
 * zusammen gesendet. Die Vorschau (`GET /api/payments/:id/bezahlt-vorschau`) sagt, was fehlt; der
 * Server prüft beim PATCH noch einmal und antwortet notfalls mit 422 `rechnungsangaben_fehlen`.
 * Beträge sind reine Anzeige: gebucht wird der Betrag aus der Datenbank, nie ein Wert von hier.
 */

type Zahlweise = "ueberweisung" | "bar" | "ec_karte";
const ZAHLWEISEN: readonly { key: Zahlweise; label: string }[] = [
  { key: "ueberweisung", label: "Überweisung" },
  { key: "bar", label: "Bar" },
  { key: "ec_karte", label: "EC-Karte" },
];

type Fehlt = RechnungsFehlt;
type KundenAngaben = RechnungsKundenAngaben;

type RechnungModus = "senden" | "freigabe_liste" | "mit_abrechnung" | "bereits_gesendet" | "neu" | "vorhandene_senden";

interface Vorschau {
  paymentId: string;
  betragCents: number;
  vorgang: { kunde: string | null; anlass: string | null; datum: string | null };
  rechnung: { modus: RechnungModus; art: string | null; empfaenger: string | null; nummer: string | null };
  /** Freigabe-Einstellung des Betriebs (Warteschlangenpunkt 34); null = unbekannt. */
  mailFreigabeModus?: "manuell" | "automatisch" | null;
  fehlt: Fehlt[];
  varianten: VarianteOption[];
  kunde: KundenAngaben | null;
}

/** Antwort des Servers auf den Klick (`KlickAntwort`, apps/api/src/lib/rechnung-bezahlt-klick.ts). */
interface KlickAntwort {
  status: "gesendet" | "bereits_gesendet" | "verworfen" | "storniert" | "wird_gesendet" | "mit_abrechnung" | "nicht_gesendet" | "versand_fehlgeschlagen" | "nicht_erstellt" | "fehler";
  art?: string | null;
  an?: string | null;
  grund?: string;
}

export interface BezahltZahlung {
  id: string;
  amountCents: number;
}

const ART_TEXT: Record<string, string> = { deposit: "Anzahlungsrechnung", final: "Schlussrechnung", invoice: "Rechnung" };
const artText = (art: string | null | undefined) => ART_TEXT[art ?? ""] ?? "Rechnung";

function heuteIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function BezahltDialog({
  open,
  zahlung,
  kundeName,
  onClose,
  onFertig,
}: {
  open: boolean;
  zahlung: BezahltZahlung | null;
  /** Anzeigename des Kunden, solange die Vorschau noch lädt. */
  kundeName?: string | null;
  onClose: () => void;
  /** Nach erfolgreichem Buchen (egal wie die Rechnung ausging) und Klick auf „Schließen“. */
  onFertig: () => void;
}) {
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [zahlweise, setZahlweise] = useState<Zahlweise>("ueberweisung");
  const [datum, setDatum] = useState(heuteIso());
  const [nichtSenden, setNichtSenden] = useState(false);
  const [variante, setVariante] = useState<string | null>(null);
  const [eingabe, setEingabe] = useState<RechnungsEingabe>(eingabeAus(null));
  const [geprueft, setGeprueft] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<KlickAntwort | null>(null);

  const zahlungId = open ? (zahlung?.id ?? null) : null;

  // Beim Öffnen alles zurücksetzen und die Vorschau holen.
  useEffect(() => {
    if (!zahlungId) return;
    let aktiv = true;
    setVorschau(null);
    setLadefehler(null);
    setErgebnis(null);
    setFehler(null);
    setGeprueft(false);
    setNichtSenden(false);
    setVariante(null);
    setZahlweise("ueberweisung");
    setDatum(heuteIso());
    apiMutate<Vorschau>(`/api/payments/${zahlungId}/bezahlt-vorschau`, "GET")
      .then((v) => {
        if (!aktiv) return;
        setVorschau(v);
        setEingabe(eingabeAus(v.kunde));
      })
      .catch((e: unknown) => {
        if (aktiv) setLadefehler(e instanceof Error ? e.message : String(e));
      });
    return () => {
      aktiv = false;
    };
  }, [zahlungId]);

  const fehlt = vorschau?.fehlt ?? [];
  const brauchtVariante = fehlt.includes("variante");
  const brauchtAdresse = fehlt.includes("adresse") || fehlt.includes("land") || fehlt.includes("email");
  const modus = vorschau?.rechnung.modus;
  // Nur wenn der Klick wirklich eine Rechnung sendet, gibt es etwas zu fragen und abzuwählen.
  const sendetRechnung = modus === "senden" || modus === "neu" || modus === "vorhandene_senden";
  const zeigeAdresse = brauchtAdresse && !nichtSenden;
  const gewaehlt = useMemo(() => vorschau?.varianten.find((v) => v.id === variante) ?? null, [vorschau, variante]);
  const betragCents = brauchtVariante ? (gewaehlt?.betragCents ?? null) : (vorschau?.betragCents ?? zahlung?.amountCents ?? null);

  const adresseOk = angabenVollstaendig(eingabe);
  const varianteOk = !brauchtVariante || variante != null;
  const gueltig = varianteOk && (!zeigeAdresse || adresseOk);

  const empfaenger = zeigeAdresse ? eingabe.email.trim() || null : (vorschau?.rechnung.empfaenger ?? null);

  async function absenden(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!zahlung || laeuft) return;
    setGeprueft(true);
    setFehler(null);
    if (!gueltig) return;
    setLaeuft(true);
    try {
      const body: Record<string, unknown> = { status: "succeeded", zahlweise, paidOn: datum };
      if (brauchtVariante && variante) body.chosenOptionId = variante;
      if (nichtSenden && sendetRechnungOderFrage()) body.rechnungNichtSenden = true;
      else if (zeigeAdresse) {
        // Das Feld Firma ist im Dialog sichtbar: immer mitsenden (leer = Firma beim Kunden bewusst leeren).
        body.rechnungsangaben = rechnungsangabenBody(eingabe);
      }
      const antwort = await apiMutateMitZusatz<unknown, { rechnung?: KlickAntwort }>(`/api/payments/${zahlung.id}`, "PATCH", body);
      setErgebnis(antwort.rechnung ?? { status: "nicht_gesendet", grund: "freigabe_liste" });
    } catch (err) {
      const neu = angabenFehlenAusFehler<VarianteOption>(err);
      if (neu && vorschau) {
        // Die Vorschau war veraltet (z. B. Adresse inzwischen gelöscht): Felder nachziehen, nicht abbrechen.
        setVorschau({ ...vorschau, fehlt: neu.fehlt, varianten: neu.varianten.length ? neu.varianten : vorschau.varianten, kunde: neu.kunde ?? vorschau.kunde });
        setFehler((err as Error).message);
      } else {
        setFehler(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLaeuft(false);
    }
  }

  function sendetRechnungOderFrage() {
    return sendetRechnung || brauchtAdresse || brauchtVariante;
  }

  if (!open || !zahlung) return null;

  if (ergebnis) {
    return (
      <Modal
        open
        onClose={onFertig}
        width="520px"
        testId="bezahlt-dialog-fertig"
        title="Zahlung ist bezahlt"
        footer={
          <Button type="button" data-testid="bezahlt-schliessen" onClick={onFertig}>
            Schließen
          </Button>
        }
      >
        <FertigText ergebnis={ergebnis} betragCents={betragCents} empfaenger={empfaenger} nummer={vorschau?.rechnung.nummer ?? null} freigabeModus={vorschau?.mailFreigabeModus ?? null} />
      </Modal>
    );
  }

  const untertitel = [vorschau?.vorgang.kunde ?? kundeName, vorschau?.vorgang.anlass].filter(Boolean).join(" · ");
  const knopf = !sendetRechnungOderFrage() || nichtSenden || modus === "freigabe_liste" ? "Bezahlt markieren" : "Bezahlt markieren und Rechnung senden";

  return (
    <Modal
      open
      onClose={onClose}
      width="560px"
      testId="bezahlt-dialog"
      title="Zahlung als bezahlt markieren"
      subtitle={untertitel || undefined}
      onSubmit={absenden}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button type="submit" data-testid="bezahlt-absenden" disabled={laeuft || !vorschau}>
            {laeuft ? "Wird gebucht …" : knopf}
          </Button>
        </>
      }
    >
      {ladefehler ? (
        <ErrorNote message={ladefehler} />
      ) : !vorschau ? (
        <p className="text-[13px] text-secondary dark:text-secondary-fixed-dim">Wird geladen …</p>
      ) : (
        <div className="space-y-4">
          {brauchtVariante ? (
            <div>
              <div className="lbl">Menü des Gastes</div>
              <VariantenKarten varianten={vorschau.varianten} gewaehlt={variante} onWahl={setVariante} name="bezahlt-menue" />
              {geprueft && !variante ? (
                <p className="mt-1.5 text-[12px] text-error" role="alert">
                  Bitte ein Menü wählen.
                </p>
              ) : (
                <p className="mt-1.5 text-[12px] text-secondary dark:text-secondary-fixed-dim">Der Betrag kommt aus dem Angebot und kann nicht geändert werden.</p>
              )}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="lbl" htmlFor="bezahlt-betrag">
                Betrag
              </label>
              <Input id="bezahlt-betrag" readOnly value={betragCents == null ? "Menü wählen" : money(betragCents)} />
            </div>
            <div>
              <label className="lbl" htmlFor="bezahlt-datum">
                Zahlungseingang am
              </label>
              <DateInput id="bezahlt-datum" value={datum} max={heuteIso()} onValueChange={(iso) => iso && setDatum(iso)} />
            </div>
          </div>

          <div>
            <div className="lbl">Zahlweise</div>
            <Segmente werte={ZAHLWEISEN} wert={zahlweise} onWahl={setZahlweise} label="Zahlweise" />
          </div>

          {zeigeAdresse ? (
            <RechnungsangabenAbschnitt eingabe={eingabe} onChange={setEingabe} geprueft={geprueft} testIdPrefix="bezahlt" />
          ) : null}

          <Vorschauhinweis freigabeModus={vorschau.mailFreigabeModus ?? null} modus={modus} nichtSenden={nichtSenden} betragCents={betragCents} empfaenger={empfaenger} art={vorschau.rechnung.art} nummer={vorschau.rechnung.nummer} />

          {sendetRechnungOderFrage() && modus !== "freigabe_liste" ? (
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px]">
              <Checkbox className="mt-0.5" checked={nichtSenden} onChange={(e) => setNichtSenden(e.target.checked)} data-testid="bezahlt-nicht-senden" />
              <span>
                <b className="font-semibold text-on-background dark:text-inverse-on-surface">Rechnung nicht senden</b>
                <br />
                <span className="text-[12px] text-secondary dark:text-secondary-fixed-dim">Die Rechnung wird erstellt und wartet in der Freigabe-Liste. Du kannst sie dort später senden.</span>
              </span>
            </label>
          ) : null}

          {fehler ? <ErrorNote message={fehler} /> : null}
        </div>
      )}
    </Modal>
  );
}

function Vorschauhinweis({
  modus,
  nichtSenden,
  betragCents,
  empfaenger,
  art,
  nummer,
  freigabeModus,
}: {
  freigabeModus: "manuell" | "automatisch" | null;
  modus: RechnungModus | undefined;
  nichtSenden: boolean;
  betragCents: number | null;
  empfaenger: string | null;
  art: string | null;
  nummer: string | null;
}) {
  const box = "flex items-start gap-2.5 rounded-xl bg-surface-container-low px-3.5 py-3 text-[13px] text-on-background dark:bg-surface-variant/20 dark:text-inverse-on-surface";
  if (modus === "mit_abrechnung") {
    return (
      <div className={box} data-testid="bezahlt-hinweis">
        <div>
          <b className="font-semibold">Rechnung kommt mit dem Abschluss der Abrechnung.</b>
          <br />
          Zu dieser Zahlung entsteht jetzt keine eigene Rechnung.
        </div>
      </div>
    );
  }
  if (modus === "freigabe_liste") {
    return (
      <div className={box} data-testid="bezahlt-hinweis">
        <div>{freigabeSatz(freigabeModus)}</div>
      </div>
    );
  }
  if (modus === "bereits_gesendet") {
    return (
      <div className={box} data-testid="bezahlt-hinweis">
        <div>
          Die {artText(art)}
          {nummer ? ` ${nummer}` : ""} wurde bereits gesendet. Es geht keine zweite Rechnung raus.
        </div>
      </div>
    );
  }
  if (nichtSenden) {
    return (
      <div className={box} data-testid="bezahlt-hinweis">
        <div>
          <b className="font-semibold">Es geht keine Rechnung raus.</b>
          <br />
          Die Rechnung wird erstellt und wartet in der Freigabe-Liste. Du kannst sie dort später senden.
        </div>
      </div>
    );
  }
  return (
    <div className={box} data-testid="bezahlt-hinweis">
      <div>
        Rechnung über <b className="font-semibold">{betragCents == null ? "…" : money(betragCents)}</b> geht sofort an <b className="font-semibold">{empfaenger ?? "die E-Mail-Adresse oben"}</b>
        <br />
        <span className="text-[12px] text-secondary dark:text-secondary-fixed-dim">{artText(art)}</span>
      </div>
    </div>
  );
}

/**
 * Warteschlangenpunkt 34: ohne Sofortversand haengt es an der Freigabe-Einstellung des Betriebs, ob die Rechnung
 * hinausgeht. Der Satz nennt den tatsaechlichen Modus; ist er unbekannt, bleibt der vorsichtige Wortlaut.
 */
function freigabeSatz(modus: "manuell" | "automatisch" | null | undefined): string {
  if (modus === "automatisch") return "Die Rechnung wird erzeugt und automatisch an den Kunden gesendet.";
  if (modus === "manuell") return "Die Rechnung wird erzeugt. Sie geht NICHT sofort hinaus, sondern wartet in der Freigabe-Liste auf deine Freigabe.";
  return "Die Rechnung wird erzeugt. Ob sie sofort hinausgeht, hängt von deiner Freigabe-Einstellung ab.";
}

function FertigText({ ergebnis, betragCents, empfaenger, nummer, freigabeModus }: { ergebnis: KlickAntwort; betragCents: number | null; empfaenger: string | null; nummer: string | null; freigabeModus: "manuell" | "automatisch" | null }) {
  const klein = "text-[13.5px] text-on-background dark:text-inverse-on-surface";
  switch (ergebnis.status) {
    case "gesendet":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          {artText(ergebnis.art)}
          {nummer ? ` ${nummer}` : ""}
          {betragCents != null ? ` über ${money(betragCents)}` : ""} ging an {ergebnis.an ?? empfaenger ?? "den Gast"}. Im Verlauf ist es vermerkt.
        </p>
      );
    case "bereits_gesendet":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Die Rechnung zu dieser Zahlung war schon beim Gast. Es wurde keine zweite gesendet.
        </p>
      );
    case "verworfen":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Die Rechnung zu dieser Zahlung wurde von euch verworfen und deshalb nicht gesendet. Die Zahlung ist bezahlt.
        </p>
      );
    case "storniert":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Die Rechnung zu dieser Zahlung ist storniert und wurde deshalb nicht gesendet. Die Zahlung ist bezahlt.
        </p>
      );
    case "wird_gesendet":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Die Zahlung ist bezahlt. Die Rechnung wird gerade gesendet, das Ergebnis steht im Verlauf des Vorgangs.
        </p>
      );
    case "mit_abrechnung":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Rechnung kommt mit dem Abschluss der Abrechnung.
        </p>
      );
    case "nicht_gesendet":
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          {ergebnis.grund === "freigabe_liste"
            ? freigabeModus === "automatisch"
              ? "Die Rechnung wurde erzeugt und geht automatisch an den Kunden."
              : freigabeModus === "manuell"
                ? "Die Rechnung wurde erzeugt und wartet in der Freigabe-Liste."
                : "Die Rechnung wurde erzeugt. Ob sie sofort hinausgeht, hängt von deiner Freigabe-Einstellung ab."
            : "Es wurde keine Rechnung gesendet. Sie wurde erstellt und wartet in der Freigabe-Liste, dort kannst du sie später senden."}
        </p>
      );
    default:
      return (
        <p className={klein} data-testid="bezahlt-ergebnis">
          Die Zahlung ist bezahlt. Die Rechnung ist aber nicht beim Gast angekommen. In der Glocke steht, was nicht geklappt hat, und von dort kannst du es erneut anstoßen.
        </p>
      );
  }
}
